export type ProjectionPreviewCapability =
  | 'checkout'
  | 'reserve'
  | 'reviews'
  | 'chat'
  | 'publish'
  | 'edit'
  | 'image_mutation'
  | 'product_id_actions'

export type ProjectionPreviewCapabilities = {
  [Capability in ProjectionPreviewCapability]: false
}

export type ProjectionPreviewActions = {
  [Capability in ProjectionPreviewCapability]: null
}

export interface ProjectionPreviewRenderContext {
  renderer: string
  renderer_version: 'projection_preview_v1'
  renderer_digest: string
  locale: string
  base_timezone: string
  visibility: 'unlisted'
  state: 'draft'
  auth_mode: 'local_offline_evaluation'
  feature_flags: {
    custom_itinerary: false
    installment_payment: false
  }
  hydration_state: 'payload_ready'
  cache_state: 'bypassed'
  product_identity: null
}

export interface ProjectionPreviewProductAttributes {
  base_timezone: string
  pricing_model: string
  visibility: 'unlisted'
  state: 'draft'
  draft_or_editing: false
  display_reviews: false
  events_count: number
  price_by_package_sequence: boolean
  minimum_checkout_tier_pricing: {
    currency: string
    guest_count: number
    price_cents: number
  }
  commercial_preview: {
    currency_exponent: number
    current_price_subunits: number
    list_price_subunits: number
  }
  current_early_bird_coupon: null | {
    code: string
    base_currency: string
    minimum_spend_cents: number
    discount_amount_cents: number
    discount_percentage: number | null
  }
  show_deposit_subtext: false
  reserve_price: null
  installment_price: null
  product_url: null
  draft_itinerary_url: null
  chatroom_id: null
}

export interface ProjectionPreviewPackageEvent {
  id: string
  name: string
  starts_at: string
  ends_at: string
  timezone: string
  checkout_tier_pricing: { price_cents: number }
  images: []
  included_in_price_html: []
  excluded_in_price_html: []
  available_slots_count: null
  available_spots_count: null
  meta: { link: null }
}

export interface ProjectionPreviewProductAttributesState {
  baseTimezone: string
  pricingModel: string
  visibility: 'unlisted'
  state: 'draft'
  draftOrEditing: false
  displayReviews: false
  eventsCount: number
  priceByPackageSequence: boolean
  minimumCheckoutTierPricing: {
    currency: string
    guestCount: number
    priceCents: number
  }
  commercialPreview: {
    currencyExponent: number
    currentPriceSubunits: number
    listPriceSubunits: number
  }
  currentEarlyBirdCoupon: null | {
    code: string
    baseCurrency: string
    minimumSpendCents: number
    discountAmountCents: number
    discountPercentage: number | null
  }
  showDepositSubtext: false
  reservePrice: null
  installmentPrice: null
  productUrl: null
  draftItineraryUrl: null
  chatroomId: null
}

export interface ProjectionPreviewPackageEventState {
  id: string
  name: string
  startsAt: string
  endsAt: string
  timezone: string
  checkoutTierPricing: { priceCents: number }
  images: []
  includedInPriceHtml: []
  excludedInPriceHtml: []
  availableSlotsCount: null
  availableSpotsCount: null
  meta: { link: null }
}

export interface ProjectionPreviewItineraryDocument {
  data: {
    type: 'itineraries'
    id: string
    attributes: { [key: string]: unknown }
    relationships: { [key: string]: unknown }
    meta?: { [key: string]: unknown }
  }
  included: Array<{ [key: string]: unknown }>
}

export interface ProjectionPreviewInput {
  schema: 'itinerary_projection_preview_input'
  schema_version: 1
  projection_digest: string
  commercial_projection_digest: string
  render_context: ProjectionPreviewRenderContext
  capabilities: ProjectionPreviewCapabilities
  hydration: {
    product_attributes: ProjectionPreviewProductAttributes
    itinerary_document: ProjectionPreviewItineraryDocument
    package_events: ProjectionPreviewPackageEvent[]
    actions: ProjectionPreviewActions
  }
  visible_claims: { [key: string]: unknown }
}

const CAPABILITIES: ProjectionPreviewCapability[] = [
  'checkout',
  'reserve',
  'reviews',
  'chat',
  'publish',
  'edit',
  'image_mutation',
  'product_id_actions',
]
const ROOT_KEYS = [
  'schema',
  'schema_version',
  'projection_digest',
  'commercial_projection_digest',
  'render_context',
  'capabilities',
  'hydration',
  'visible_claims',
]
const RENDER_CONTEXT_KEYS = [
  'renderer',
  'renderer_version',
  'renderer_digest',
  'locale',
  'base_timezone',
  'visibility',
  'state',
  'auth_mode',
  'feature_flags',
  'hydration_state',
  'cache_state',
  'product_identity',
]
const FEATURE_FLAG_KEYS = ['custom_itinerary', 'installment_payment']
const HYDRATION_KEYS = ['product_attributes', 'itinerary_document', 'package_events', 'actions']
const PRODUCT_ATTRIBUTE_KEYS = [
  'base_timezone',
  'pricing_model',
  'visibility',
  'state',
  'draft_or_editing',
  'display_reviews',
  'events_count',
  'price_by_package_sequence',
  'minimum_checkout_tier_pricing',
  'commercial_preview',
  'current_early_bird_coupon',
  'show_deposit_subtext',
  'reserve_price',
  'installment_price',
  'product_url',
  'draft_itinerary_url',
  'chatroom_id',
]
const PACKAGE_EVENT_KEYS = [
  'id',
  'name',
  'starts_at',
  'ends_at',
  'timezone',
  'checkout_tier_pricing',
  'images',
  'included_in_price_html',
  'excluded_in_price_html',
  'available_slots_count',
  'available_spots_count',
  'meta',
]
const SHA256_HEX = /^[0-9a-f]{64}$/

type UnknownObject = { [key: string]: unknown }

const isObject = (value: unknown): value is UnknownObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const hasExactKeys = (value: UnknownObject, keys: string[]): boolean =>
  Object.keys(value).sort().join() === keys.slice().sort().join()

const isNonBlankString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0

const isPositiveInteger = (value: unknown): value is number => Number.isInteger(value) && Number(value) > 0

const isNonNegativeInteger = (value: unknown): value is number => Number.isInteger(value) && Number(value) >= 0

const validateProductAttributes = (value: unknown): value is ProjectionPreviewProductAttributes => {
  if (!isObject(value) || !hasExactKeys(value, PRODUCT_ATTRIBUTE_KEYS)) return false

  const pricing = value.minimum_checkout_tier_pricing
  const commercial = value.commercial_preview
  const coupon = value.current_early_bird_coupon
  const nullableProductFields = [
    value.reserve_price,
    value.installment_price,
    value.product_url,
    value.draft_itinerary_url,
    value.chatroom_id,
  ]
  const validPricing =
    isObject(pricing) &&
    hasExactKeys(pricing, ['currency', 'guest_count', 'price_cents']) &&
    typeof pricing.currency === 'string' &&
    /^[A-Z]{3}$/.test(pricing.currency) &&
    isPositiveInteger(pricing.guest_count) &&
    isPositiveInteger(pricing.price_cents)
  const validCommercial =
    isObject(commercial) &&
    hasExactKeys(commercial, ['currency_exponent', 'current_price_subunits', 'list_price_subunits']) &&
    isNonNegativeInteger(commercial.currency_exponent) &&
    isPositiveInteger(commercial.current_price_subunits) &&
    isPositiveInteger(commercial.list_price_subunits)
  const validCoupon =
    coupon === null ||
    (isObject(coupon) &&
      hasExactKeys(coupon, [
        'code',
        'base_currency',
        'minimum_spend_cents',
        'discount_amount_cents',
        'discount_percentage',
      ]) &&
      isNonBlankString(coupon.code) &&
      typeof coupon.base_currency === 'string' &&
      /^[A-Z]{3}$/.test(coupon.base_currency) &&
      isNonNegativeInteger(coupon.minimum_spend_cents) &&
      isPositiveInteger(coupon.discount_amount_cents) &&
      (coupon.discount_percentage === null || isPositiveInteger(coupon.discount_percentage)))

  return (
    isNonBlankString(value.base_timezone) &&
    isNonBlankString(value.pricing_model) &&
    value.visibility === 'unlisted' &&
    value.state === 'draft' &&
    value.draft_or_editing === false &&
    value.display_reviews === false &&
    isPositiveInteger(value.events_count) &&
    typeof value.price_by_package_sequence === 'boolean' &&
    validPricing &&
    validCommercial &&
    validCoupon &&
    value.show_deposit_subtext === false &&
    nullableProductFields.every(field => field === null)
  )
}

const validatePackageEvent = (value: unknown): value is ProjectionPreviewPackageEvent => {
  if (!isObject(value) || !hasExactKeys(value, PACKAGE_EVENT_KEYS)) return false

  const price = value.checkout_tier_pricing
  const meta = value.meta
  return (
    isNonBlankString(value.id) &&
    isNonBlankString(value.name) &&
    isNonBlankString(value.starts_at) &&
    isNonBlankString(value.ends_at) &&
    isNonBlankString(value.timezone) &&
    isObject(price) &&
    hasExactKeys(price, ['price_cents']) &&
    isPositiveInteger(price.price_cents) &&
    Array.isArray(value.images) &&
    value.images.length === 0 &&
    Array.isArray(value.included_in_price_html) &&
    value.included_in_price_html.length === 0 &&
    Array.isArray(value.excluded_in_price_html) &&
    value.excluded_in_price_html.length === 0 &&
    value.available_slots_count === null &&
    value.available_spots_count === null &&
    isObject(meta) &&
    hasExactKeys(meta, ['link']) &&
    meta.link === null
  )
}

const validateItineraryDocument = (value: unknown): value is ProjectionPreviewItineraryDocument => {
  if (!isObject(value) || !hasExactKeys(value, ['data', 'included'])) return false
  if (!isObject(value.data) || !Array.isArray(value.included)) return false

  const data = value.data
  return (
    data.type === 'itineraries' &&
    isNonBlankString(data.id) &&
    isObject(data.attributes) &&
    isObject(data.relationships) &&
    (data.meta === undefined || isObject(data.meta)) &&
    value.included.every(isObject)
  )
}

const validationError = (value: unknown): string | null => {
  if (!isObject(value) || !hasExactKeys(value, ROOT_KEYS)) {
    return 'unsupported projection preview hydration contract'
  }
  if (value.schema !== 'itinerary_projection_preview_input' || value.schema_version !== 1) {
    return 'unsupported projection preview hydration contract'
  }

  const renderContext = value.render_context
  if (
    !isObject(renderContext) ||
    !hasExactKeys(renderContext, RENDER_CONTEXT_KEYS) ||
    !isNonBlankString(renderContext.renderer) ||
    renderContext.renderer_version !== 'projection_preview_v1' ||
    typeof renderContext.renderer_digest !== 'string' ||
    !SHA256_HEX.test(renderContext.renderer_digest) ||
    !isNonBlankString(renderContext.locale) ||
    !isNonBlankString(renderContext.base_timezone) ||
    renderContext.product_identity !== null ||
    renderContext.visibility !== 'unlisted' ||
    renderContext.state !== 'draft' ||
    renderContext.auth_mode !== 'local_offline_evaluation' ||
    !isObject(renderContext.feature_flags) ||
    !hasExactKeys(renderContext.feature_flags, FEATURE_FLAG_KEYS) ||
    Object.values(renderContext.feature_flags).some(flag => flag !== false) ||
    renderContext.hydration_state !== 'payload_ready' ||
    renderContext.cache_state !== 'bypassed' ||
    typeof value.projection_digest !== 'string' ||
    !SHA256_HEX.test(value.projection_digest) ||
    typeof value.commercial_projection_digest !== 'string' ||
    !SHA256_HEX.test(value.commercial_projection_digest)
  ) {
    return 'invalid projection preview render context'
  }

  const capabilities = value.capabilities
  const hydration = value.hydration
  if (!isObject(hydration) || !hasExactKeys(hydration, HYDRATION_KEYS)) {
    return 'incomplete projection preview hydration contract'
  }
  const actions = hydration.actions
  if (
    !isObject(capabilities) ||
    !isObject(actions) ||
    !hasExactKeys(capabilities, CAPABILITIES) ||
    !hasExactKeys(actions, CAPABILITIES)
  ) {
    return 'projection preview capabilities are not complete'
  }
  for (const capability of CAPABILITIES) {
    if (capabilities[capability] !== false || actions[capability] !== null) {
      return `projection preview capability ${capability} must be disabled`
    }
  }

  const packageEvents = hydration.package_events
  const productAttributes = hydration.product_attributes
  if (
    !validateProductAttributes(productAttributes) ||
    !validateItineraryDocument(hydration.itinerary_document) ||
    !Array.isArray(packageEvents) ||
    packageEvents.length === 0 ||
    !packageEvents.every(validatePackageEvent) ||
    !isObject(value.visible_claims)
  ) {
    return 'incomplete projection preview hydration contract'
  }
  if (productAttributes.events_count !== packageEvents.length) {
    return 'incomplete projection preview hydration contract'
  }

  return null
}

export const isProjectionPreviewInput = (value: unknown): value is ProjectionPreviewInput =>
  validationError(value) === null

export const parseProjectionPreviewInput = (value: unknown): ProjectionPreviewInput => {
  const error = validationError(value)
  if (error) throw new Error(error)

  return value as ProjectionPreviewInput
}
